<template>
  <ECard
    as="a"
    :elevation="0"
    :href="url"
    :class="classes"
    :target="linkTarget"
    :data-rank="rank"
    :aria-label="ariaLabel || null"
    rel="noopener"
    data-test-id="snippet-card"
    @click="$emit('click')"
    @auxclick="$emit('auxclick')"
  >
    <slot v-if="$slots.thumbnail" name="thumbnail" />
    <ESnippetThumbnail
      v-else
      class="snippet-card__thumbnail"
      :src="adjustedThumbnailUrl"
      :duration="duration"
    />
    <div class="snippet-card__content">
      <slot name="title-start" />
      <EHeading
        as="h3"
        class="snippet-card__title"
        data-test-id="snippet-card-title"
      >
        {{ title }}
      </EHeading>
      <slot name="title-end" />
    </div>
    <div v-if="$slots.default" class="snippet-card__footer">
      <slot />
    </div>
  </ECard>
</template>

<script>
import { mapGetters } from 'vuex';

import classes from '~~/common/vue/mixins/classes';
import ECard from '~~/common/vue/components/card';
import EHeading from '~~/common/vue/components/heading';
import { adjustThumbnailSize } from '~~/search/common/js/universal/urls';
import ESnippetThumbnail from '~~/search/common/vue/components/snippets/thumbnail';

const THUMBNAIL_WIDTH = 300;

export default {
  name: 'ESnippetCard',
  components: {
    ECard,
    EHeading,
    ESnippetThumbnail,
  },
  mixins: [classes],
  props: {
    duration: ESnippetThumbnail.props.duration,
    url: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    thumbnailUrl: {
      type: String,
      default: null,
    },
    compact: {
      type: Boolean,
      cssClass: true,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    rank: {
      type: Number,
      default: null,
    },
  },
  computed: {
    ...mapGetters('user', ['linkTarget']),
    adjustedThumbnailUrl() {
    // ENewsSnippet and EVideosSnippet bind the whole "item" object,
    // which will automatically map thumbnailUrl & proxiedThumbnailUrl properties.
      return adjustThumbnailSize(this.thumbnailUrl, { width: THUMBNAIL_WIDTH });
    },
  },
};
</script>

<style lang="scss" scoped>
.snippet-card {
  border: $border-width solid var(--color-decorative-border-1);
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--color-background-primary);
}

.snippet-card__content,
.snippet-card__footer {
  padding: $space-1s $space-s;

  @include tablet {
    padding: $space-s $space-m;

    .snippet-card--compact & {
      padding: $space-1s $space-s;
    }
  }
}

.snippet-card__content {
  padding-bottom: $space-2s;
  flex-grow: 1;

  &:last-child {
    padding-bottom: $space-m;
  }

  + .snippet-card__footer {
    padding-top: 0;
  }
}

.snippet-card__thumbnail {
  border-radius: $border-radius-l $border-radius-l 0 0;
}

.snippet-card__title {
  @include line-clamp(4);

  @include desktop {
    @include line-clamp(3);
  }

  font-size: $font-m;
  font-weight: $font-weight-m;
  color: var(--color-link-results-default);

  &:not(:first-child) {
    margin-top: $space-2s;
  }

  .snippet-card:hover &,
  .snippet-card:focus & {
    text-decoration: underline;
  }

  .snippet-card:visited & {
    color: var(--color-link-results-visited);
  }
}
</style>
